import SalesTracking from 'libs/api/admin/salesTrackings';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFilter } from 'hooks/useFilter';
import { useRecoilValue } from 'recoil';
import { getShoppingCenters } from 'hooks/useUser';
import { useTableChangeFilterHook } from 'hooks/useTableChangeFilterHook';
import { useTranslationUi } from 'hooks/useTranslationUi';
import { useApi } from 'hooks/useApi';

import { salesTrackingAdapter } from './salesTracking.adapter';
import { getShops, getInactiveShops } from '../../../hooks/useShops';
import UIHelper from '../../../helpers/UIHelper';
import { Form } from 'antd';
import ErrorHelper from '../../../helpers/ErrorHelper';
import { useLocation, useNavigate } from 'react-router-dom';

const api = new SalesTracking();

const defaultState = {
  salesTracking: [],
  pagination: {
    current: 1,
    pageSize: 10,
    position: ['bottomCenter'],
    hideOnSinglePage: true,
    showSizeChanger: false,
  },
  sorting: {
    field: '',
    order: '',
  },
};

export const useSalesTracking = (isMainPage = true, isEdit = false) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const shops = useRecoilValue(getShops);
  const inactiveShops = useRecoilValue(getInactiveShops);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [updateInProcess, setUpdateInProcess] = useState(false);
  const [data, setData] = useState(defaultState);
  const { getBooleanText } = useTranslationUi();
  const onChangeTable = useTableChangeFilterHook();
  const { handleChangeFilters, handleCloseFilter, searchParams, filterData } =
    useFilter();
  const { handleDownloadExcel } = useApi({
    resourceKey: 'sales_trackings',
    singleResourceKey: 'sales_tracking',
    api,
    isMainPage,
  });

  const filters = useMemo(() => {
    return [
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'shopping_center_id',
        values:
          filterData?.shopping_centers &&
          filterData?.shopping_centers.map((i) => ({
            value: i.value,
            title: i.title,
          })),
      },
      {
        title: t('Insegna'),
        type: 'select',
        name: 'company_id',
        isSearch: true,
        values:
          filterData?.companies &&
          filterData?.companies.map((i) => ({
            value: i.value,
            title: i.title,
          })),
        inactiveValues:
          filterData?.inactive_companies &&
          filterData?.inactive_companies.map((i) => ({
            value: i.value,
            title: i.title,
          })),
      },
      {
        title: t('Date range'),
        type: 'range',
        name: 'dateRange',
        keys: ['date_from', 'date_to'],
      },
    ];
  }, [t, filterData]);

  const getQueryParams = () => {
    const params = {
      per: data.pagination.pageSize,
      page: data.pagination.current,
      ...Object.fromEntries([...searchParams]),
    };
    if (data.sorting.order) {
      params.sortable_column = data.sorting.field;
      params.direction = data.sorting.order;
    }

    return params;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const {
        data: { sales_trackings = [], pagination = { total_count: 0 } },
      } = await api.list({
        params: getQueryParams(),
      });
      setData((s) => ({
        ...s,
        salesTracking: salesTrackingAdapter(sales_trackings || []),
        pagination: {
          ...defaultState.pagination,
          ...pagination,
          total: pagination?.total_count,
        },
      }));
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isMainPage) {
      fetchData();
    }
  }, [isMainPage, searchParams, data.pagination.current, data.sorting.order]);

  const onChangeTableFilter = useCallback(
    (paginate, sort, filter) => {
      onChangeTable(setData, paginate, sort, filter);
    },
    [onChangeTable],
  );

  const columns = useMemo(() => {
    return [
      {
        title: t('Village'),
        dataIndex: 'shopping_centers.title',
        sorter: () => {},
      },
      {
        title: t('Infopoint'),
        dataIndex: 'by_infopoint',
        sorter: () => {},
        width: 130,
        render: (row) => getBooleanText(row),
      },
      {
        title: t('Insegna'),
        dataIndex: 'companies.title',
        sorter: () => {},
      },
      {
        title: t('Importo'),
        dataIndex: 'amount',
        width: 130,
        sorter: () => {},
      },
      {
        title: t('Data'),
        dataIndex: 'created_at',
        sorter: () => {},
        render: (r) =>
          UIHelper.formatedDateWithTimezone(r, 'DD MMM YYYY HH:mm'),
      },
      {
        title: t('Sconto Village'),
        dataIndex: 'discount',
        sorter: () => {},
        width: 160,
        render: (row) => getBooleanText(row),
      },
      {
        title: t('Loyalty'),
        dataIndex: 'customer_code',
        sorter: () => {},
        width: 130,
        render: (row) => getBooleanText(row),
      },
    ];
  }, [t, getBooleanText]);

  const handleCreateOrUpdate = useCallback(async (values) => {
    setUpdateInProcess(true);
    try {
      const { sales_trackings_type, ...rest } = values;
      const preparedData = {
        ...rest,
        sales_trackings_type: sales_trackings_type ? 'anonymous' : 'normal',
      };
      await api.create({
        data: preparedData,
      });
      navigate(UIHelper.getListRoute(pathname, 1));
    } catch (e) {
      form.setFields(
        ErrorHelper.getErrorsForForm(e?.response?.data || {}, {}),
      );
    } finally {
      setUpdateInProcess(false);
    }
  }, [form, pathname, navigate]);

  const countries = filterData?.countries || [];
  const regions = filterData?.regions || [];

  return {
    loading,
    columns,
    handleCloseFilter,
    handleChangeFilters,
    filters,
    data,
    onChangeTableFilter,
    handleDownloadExcel,
    form,
    handleCreateOrUpdate,
    updateInProcess,
    shops,
    inactiveShops,
    regions,
    countries,
  };
};
